/* ============================================================
   Living Juice — shared animation controller
   ------------------------------------------------------------
   One script, included (via defer) by every lj- section that
   uses data-animate / data-countup. Safe to include from
   multiple sections on the same page — window.__ljAnimateInit
   guards against setting up duplicate observers; document-wide
   queries mean whichever section loads first still covers every
   other section's elements once the DOM is ready.

   Two independent, dependency-free behaviours:
     1. Scroll reveal  — [data-animate] elements get .is-visible
        added (once) via IntersectionObserver when scrolled into
        view. data-animate-delay="ms" staggers the reveal.
     2. Count-up       — [data-countup] elements tween their text
        from 0 to [data-countup-to] (+ optional
        [data-countup-suffix]) once scrolled into view.

   Both fall back to an instant, un-animated state when the user
   has prefers-reduced-motion set, or IntersectionObserver isn't
   available.

   IMPORTANT: lj-animate.css only ever hides [data-animate] elements
   while html.lj-anim-ready is present — added below, by this script,
   the instant it runs. Content is NEVER hidden by CSS alone, so if
   this script fails to load/execute for any reason, every section
   still renders fully visible (just without the reveal animation)
   instead of staying invisible forever.
   ============================================================ */
(function () {
  'use strict';
  document.documentElement.classList.add('lj-anim-ready');

  if (window.__ljAnimateInit) return;
  window.__ljAnimateInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal() {
    var els = document.querySelectorAll('[data-animate]:not(.is-visible)');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-animate-delay'), 10) || 0;
        setTimeout(function () { el.classList.add('is-visible'); }, delay);
        obs.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  function initCountUp() {
    var els = document.querySelectorAll('[data-countup]:not([data-countup-done])');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-countup-done', '');
        el.textContent = el.getAttribute('data-countup-to') + (el.getAttribute('data-countup-suffix') || '');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.setAttribute('data-countup-done', '');
        obs.unobserve(el);
        var to = parseFloat(el.getAttribute('data-countup-to')) || 0;
        var suffix = el.getAttribute('data-countup-suffix') || '';
        var duration = 1800, start = null;
        function tick(ts) {
          if (!start) start = ts;
          var progress = Math.min(1, (ts - start) / duration);
          var eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = Math.round(to * eased) + suffix;
          if (progress < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  function init() {
    initReveal();
    initCountUp();
    // Belt-and-suspenders: whatever happens, nothing stays invisible
    // for more than 4s — covers any edge case the observer might miss.
    setTimeout(function () {
      document.querySelectorAll('[data-animate]:not(.is-visible)').forEach(function (el) {
        el.classList.add('is-visible');
      });
    }, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-run when a section is re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
